import { useState, useEffect } from "react";
import {
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Avatar,
} from "@mui/material";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import { useNavigate, useLocation, Link as RouterLink } from "react-router-dom";
import useAuth from "../hooks/useAuth";
import { canAccessAdminArea, hasPermission } from "../utils/permissions";
import { loginPathWithNext } from "../utils/loginNext";
import { badgesFromPlayer } from "../utils/usernameBadges";
import UsernameBadges from "./UsernameBadges";

export default function UserMenu() {
  const { player, isAuthenticated, loading, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState(null);
  const [headImgFailed, setHeadImgFailed] = useState(false);

  useEffect(() => {
    setHeadImgFailed(false);
  }, [player?.minecraftUuid, player?.minecraftSkinTextureHash]);

  if (loading) return null;

  if (!isAuthenticated) {
    return (
      <Button
        component={RouterLink}
        to={loginPathWithNext(location.pathname, location.search)}
        sx={{ color: "white", ml: 1 }}
      >
        Log In
      </Button>
    );
  }

  const handleOpen = (e) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleLogout = async () => {
    handleClose();
    await logout();
  };

  const headCandidate = player.minecraftUuid
    ? `/uploads/minecraft-heads/${player.minecraftUuid}.png`
    : null;

  const menuBadges = badgesFromPlayer(player);

  return (
    <Box sx={{ display: "flex", alignItems: "center", ml: 1 }}>
      {/* Desktop: show username */}
      <Box
        sx={{
          display: { xs: "none", sm: "flex" },
          alignItems: "center",
          mr: 0.5,
          minWidth: 0,
        }}
      >
        <Typography variant="body2" sx={{ color: "white" }}>
          {player.minecraftUsername || player.email}
        </Typography>
        <UsernameBadges verified={menuBadges.verified} supporter={menuBadges.supporter} />
      </Box>
      <IconButton onClick={handleOpen} sx={{ p: 0.5 }}>
        {player.minecraftUuid ? (
          <Avatar
            src={headCandidate && !headImgFailed ? headCandidate : undefined}
            alt={player.minecraftUsername || player.email}
            sx={{ width: 32, height: 32, bgcolor: "transparent", borderRadius: 0 }}
            imgProps={{
              onError: () => setHeadImgFailed(true),
              style: { objectFit: "contain" },
            }}
          >
            <AccountCircleIcon sx={{ color: "white", fontSize: 32 }} />
          </Avatar>
        ) : (
          <AccountCircleIcon sx={{ color: "white", fontSize: 32 }} />
        )}
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem disabled>
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, display: "inline-flex", alignItems: "center" }}
          >
            {player.minecraftUsername || player.email}
            <UsernameBadges verified={menuBadges.verified} supporter={menuBadges.supporter} />
          </Typography>
        </MenuItem>
        {player.staticWebUsername && hasPermission(player, "profiles.view") ? (
          <MenuItem
            component={RouterLink}
            to={`/u/${player.staticWebUsername}`}
            onClick={handleClose}
          >
            My Profile
          </MenuItem>
        ) : null}
        {hasPermission(player, "messaging.view") ? (
          <MenuItem onClick={() => { handleClose(); navigate("/messages"); }}>
            Messages
          </MenuItem>
        ) : null}
        <MenuItem onClick={() => { handleClose(); navigate("/submissions"); }}>
          Submissions
        </MenuItem>
        <MenuItem onClick={() => { handleClose(); navigate("/my-upload"); }}>
          Upload Center
        </MenuItem>
        <MenuItem onClick={() => { handleClose(); navigate("/my-account"); }}>
          Account Settings
        </MenuItem>
        {canAccessAdminArea(player) && (
          <MenuItem onClick={() => { handleClose(); navigate("/admin"); }}>
            Admin Panel
          </MenuItem>
        )}
        <MenuItem onClick={handleLogout}>Log Out</MenuItem>
      </Menu>
    </Box>
  );
}
